import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { PickpointStationService } from '../../_services/pickpoint-station.service';
import { PickpointStation, PickpointShipment } from '../../_models/shipping-config.interface';

@Component({
  standalone: true,
  imports: [CommonModule, RouterModule],
  selector: 'hawk-pickpoint-station-show',
  template: `
    <div class="page-header flex flex-row px-4 py-2 gap-2">
      <div class="flex-1 bm-breadcrumbs text-sm">
        <ul class="m-0 p-0">
          <li><a routerLink="/a">Home</a></li>
          <li><a routerLink="/pickpoint_stations">PickPoint-Stationen</a></li>
          <li>{{ station()?.name }}</li>
        </ul>
      </div>
      @if (station()) {
        <button class="bm-btn bm-btn-sm bm-btn-error"
          [disabled]="isDeleting()"
          (click)="onDelete()">
          {{ isDeleting() ? 'Lösche…' : 'Löschen' }}
        </button>
      }
    </div>

    @if (deleteError(); as err) {
      <div class="bm-alert bm-alert-error bm-alert-soft mx-4 mt-2">{{ err }}</div>
    }

    <div class="page-content page-frame overflow-y-auto flex flex-col gap-4">
      @if (station(); as s) {
        <div class="page-sheet max-w-4xl mx-auto grid grid-cols-2 gap-4">
          <!-- Stammdaten -->
          <div class="bm-card bm-card-sm bm-card-body bm-card-border border-base-300">
            <div class="bm-card-title bm-card-title-top bg-base-100">Stationsdaten</div>
            <dl class="bm-dl gap-x-2">
              <dt>PP-ID</dt>
              <dd><span class="bm-badge bm-badge-neutral">{{ s.pp_id }}</span></dd>
              <dt>Name</dt>
              <dd>{{ s.name }}</dd>
              <dt>Status</dt>
              <dd>
                @if (s.active) {
                  <span class="bm-badge bm-badge-xs bm-badge-success">Aktiv</span>
                } @else {
                  <span class="bm-badge bm-badge-xs bm-badge-error">Inaktiv</span>
                }
              </dd>
              <dt>Meldetyp</dt>
              <dd>{{ s.report_type || '-' }}</dd>
              <dt>Oeffnungszeiten</dt>
              <dd>{{ s.opening_hours || '-' }}</dd>
            </dl>
          </div>

          <!-- Adresse -->
          <div class="bm-card bm-card-sm bm-card-body bm-card-border border-base-300">
            <div class="bm-card-title bm-card-title-top bg-base-100">Adresse</div>
            <dl class="bm-dl gap-x-2">
              <dt>Strasse</dt>
              <dd>{{ s.address_street }}</dd>
              <dt>PLZ / Ort</dt>
              <dd>{{ s.address_zip }} {{ s.address_city }}</dd>
              <dt>Land</dt>
              <dd>{{ s.address_country }}</dd>
              @if (s.address_latitude && s.address_longitude) {
                <dt>Koordinaten</dt>
                <dd>{{ s.address_latitude }}, {{ s.address_longitude }}</dd>
              }
              @if (s.activation_date) {
                <dt>Aktivierung</dt>
                <dd>{{ s.activation_date }}</dd>
              }
              @if (s.cancellation_date) {
                <dt>Kuendigung</dt>
                <dd>{{ s.cancellation_date }}</dd>
              }
            </dl>
          </div>
        </div>

        <!-- Sendungen -->
        <div class="page-sheet max-w-4xl mx-auto">
          <div class="bm-card bm-card-sm bm-card-body bm-card-border border-base-300">
            <div class="bm-card-title bm-card-title-top bg-base-100">
              Sendungen ({{ shipments().length }})
            </div>
            @if (shipments().length === 0) {
              <div class="text-sm opacity-60 py-2">Keine Sendungen vorhanden</div>
            } @else {
              <div class="overflow-x-auto">
                <table class="table table-xs">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Lieferschein</th>
                      <th>Operation</th>
                      <th>Status</th>
                      <th>Versandt am</th>
                      <th>Artikel</th>
                      <th>Staging</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (sh of shipments(); track sh.id) {
                      <tr>
                        <td>{{ sh.id }}</td>
                        <td>{{ sh.delivery_note_number || '-' }}</td>
                        <td>{{ sh.operation_id || '-' }}</td>
                        <td>
                          <span class="bm-badge bm-badge-xs" [class]="statusBadge(sh.status)">
                            {{ statusLabel(sh.status) }}
                          </span>
                        </td>
                        <td>{{ sh.shipped_at ? (sh.shipped_at | date:'dd.MM.yy HH:mm') : '-' }}</td>
                        <td>{{ sh.articles_count }}</td>
                        <td>{{ sh.staging_records_count }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        </div>
      }
    </div>
  `
})
export class PickpointStationShowComponent implements OnInit {
  station = signal<PickpointStation | null>(null);
  shipments = signal<PickpointShipment[]>([]);
  isDeleting = signal<boolean>(false);
  deleteError = signal<string | null>(null);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private stationService: PickpointStationService,
  ) {}

  ngOnInit() {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.stationService.getStation(id).subscribe(data => {
      this.station.set(data.station);
      this.shipments.set(data.shipments);
    });
  }

  async onDelete() {
    const s = this.station();
    if (!s) return;
    if (!confirm(`Station "${s.name}" (PP-${s.pp_id}) wirklich endgültig löschen?`)) return;

    this.deleteError.set(null);
    this.isDeleting.set(true);
    try {
      await firstValueFrom(this.stationService.destroyStation(s.id));
      // Pfad ohne /a/-Prefix — der wird via <base href="/a/"> automatisch
      // hinzugefuegt. routerLink im Breadcrumb nutzt dasselbe Schema.
      await this.router.navigate(['/pickpoint_stations']);
    } catch (err) {
      const httpErr = err as HttpErrorResponse;
      this.deleteError.set(httpErr?.error?.error || 'Station konnte nicht gelöscht werden.');
    } finally {
      this.isDeleting.set(false);
    }
  }

  statusBadge(status: string): string {
    switch (status) {
      case 'prepared': return 'bm-badge-neutral';
      case 'shipped': return 'bm-badge-info';
      case 'delivered': return 'bm-badge-warning';
      case 'picked_up': return 'bm-badge-success';
      default: return '';
    }
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'prepared': return 'Vorbereitet';
      case 'shipped': return 'Versandt';
      case 'delivered': return 'Zugestellt';
      case 'picked_up': return 'Abgeholt';
      default: return status;
    }
  }
}
